import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { ACTIVATION_KEYS_SEARCH_DEBOUNCE_MS } from '../../../../constants/activationKeys';
import { getVMActivationKeys } from '../../data/actions/actions';
import { ACTIVATION_KEY_URL_PARAM, queryForKey, queryForPage, queryForSearch } from './activationCodes';
import SupportVMActivationCodesTable from './supportVMActivationCodesTable';
import SupportVMActivationCodesIncrementModal from './supportVMActivationCodesIncrementModal';
import ActivationHistoryModal from './supportVMActivationCodesHistoryModal';

const SupportVMActivationCodes = ({ loading, activationKeys, pagination, getVMActivationKeys }) => {
	const [searchParams] = useSearchParams();
	const keyFromUrl = searchParams.get(ACTIVATION_KEY_URL_PARAM);
	const [queryParams, setQueryParams] = useState(() => { return queryForKey(keyFromUrl); });
	const [tableVersion, setTableVersion] = useState(0);
	const appliedKeyFromUrl = useRef(keyFromUrl);
	const searchTimer = useRef(null);
	const [showIncrementModal, toggleIncrementModal] = useState(false);
	const [productKey, setProductKey] = useState(null);
	const [showHistoryModal, toggleHistoryModal] = useState(false);
	const [activationHistory, setActivationHistory] = useState(null);
	const [extraActivations, setExtraActivations] = useState(null);

	/*
	 * A key in the URL comes from a machine's card and wins over whatever was typed. The table is
	 * remounted with it because the search box reads its starting text once, on mount. The sidebar
	 * navigates to the bare path right after the page opens, and that absence changes nothing here:
	 * the key stays in the box, and a later card click brings a new one.
	 */
	useEffect(() => {
		if (keyFromUrl === null || keyFromUrl === appliedKeyFromUrl.current) {
			return;
		}

		appliedKeyFromUrl.current = keyFromUrl;
		setQueryParams(queryForKey(keyFromUrl));
		setTableVersion((version) => { return version + 1; });
	}, [keyFromUrl]);

	useEffect(() => {
		getVMActivationKeys(queryParams);
	}, [queryParams]);

	useEffect(() => {
		return () => { clearTimeout(searchTimer.current); };
	}, []);

	const handleTableChange = (type, { page, sizePerPage, searchText }) => {
		if (type === 'pagination') {
			setQueryParams((current) => { return queryForPage(current, page, sizePerPage); });
		}

		if (type === 'search') {
			clearTimeout(searchTimer.current);
			searchTimer.current = setTimeout(() => {
				setQueryParams((current) => { return queryForSearch(current, searchText); });
			}, ACTIVATION_KEYS_SEARCH_DEBOUNCE_MS);
		}
	};

	const openIncrementModal = (productKey) => {
		setProductKey(productKey);
		toggleIncrementModal(true);
	};

	const openActivationsHistoryModal = (row) => {
		setActivationHistory(row.activations);
		setExtraActivations(row.extraActivations);
		toggleHistoryModal(true);
	};

	return (
		<>
			<SupportVMActivationCodesTable
				key={tableVersion}
				loading={loading}
				activationKeys={activationKeys}
				pagination={pagination}
				queryParams={queryParams}
				onTableChange={handleTableChange}
				openIncrementModal={openIncrementModal}
				openActivationsHistoryModal={openActivationsHistoryModal}
			/>
			<SupportVMActivationCodesIncrementModal
				isOpen={showIncrementModal}
				toggleModal={toggleIncrementModal}
				productKey={productKey}
				onIncreased={() => { getVMActivationKeys(queryParams); }}
			/>
			<ActivationHistoryModal
				isOpen={showHistoryModal}
				toggleModal={toggleHistoryModal}
				activationHistory={activationHistory}
				extraActivations={extraActivations}
				className="modal-dialog modal-lg"
				classNameModalHeader="modal-header"
			/>
		</>
	);
};

const mapStateToProps = (state) => {
	const { activationKeys, pagination, loading } = state.activationKeys;

	return {
		activationKeys,
		pagination,
		loading
	};
};

export default connect(mapStateToProps, {
	getVMActivationKeys
})(SupportVMActivationCodes);
